import React, { useState, useEffect } from "react";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import SyncIcon from "@mui/icons-material/Sync";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { API_BASE_URL } from "../config/api";
import { useAuth } from "../contexts/AuthContext";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type BadgeType =
  | "first_win"
  | "giant_killer"
  | "win_streak_10"
  | "division_champion"
  | "close_call"
  | "perfect_day"
  | "veteran"
  | "partner_carry"
  | "clean_sweep"
  | "mr_ms_consistent"
  | "century"
  | "iron_man"
  | "all_season_warrior"
  | "division_hopper"
  | "unlucky"
  | "sp_team";

interface BadgeInfo {
  name: string;
  description: string;
  emoji: string;
  milestone: boolean; // Milestone badges are never reset
}

const BADGE_CATALOGUE: Record<BadgeType, BadgeInfo> = {
  first_win: {
    name: "First Victory",
    description: "Win your first match",
    emoji: "🎉",
    milestone: false,
  },
  giant_killer: {
    name: "Giant Killer",
    description: "Defeat a player ranked 5+ positions higher than you",
    emoji: "⚔️",
    milestone: false,
  },
  win_streak_10: {
    name: "Unstoppable",
    description: "Win 10 individual matches in a row",
    emoji: "🔥",
    milestone: false,
  },
  close_call: {
    name: "Close Call",
    description: "Win a match by a narrow margin (4-3 or 5-4)",
    emoji: "💪",
    milestone: false,
  },
  perfect_day: {
    name: "Perfect Day",
    description: "Win all 4 matches in a single league day",
    emoji: "⭐",
    milestone: false,
  },
  veteran: {
    name: "Veteran",
    description: "Play 50+ league days in your division",
    emoji: "🏅",
    milestone: false,
  },
  partner_carry: {
    name: "Partner Carry",
    description: "Win with a partner ranked 10+ positions lower than you",
    emoji: "🎯",
    milestone: false,
  },
  clean_sweep: {
    name: "Clean Sweep",
    description: "Win a match 5-0 or 6-0",
    emoji: "🧹",
    milestone: false,
  },
  mr_ms_consistent: {
    name: "Mr/Ms Consistent",
    description: "Win exactly 2 games in each of 5 consecutive matches",
    emoji: "🎲",
    milestone: false,
  },
  century: {
    name: "Century",
    description:
      "Win 100+ games in a season with a positive game difference",
    emoji: "💯",
    milestone: false,
  },
  iron_man: {
    name: "Iron Man",
    description: "Play 10 consecutive league days",
    emoji: "🦾",
    milestone: false,
  },
  division_hopper: {
    name: "Division Hopper",
    description: "Play competitive matches in 3+ different divisions",
    emoji: "🔀",
    milestone: false,
  },
  unlucky: {
    name: "Unlucky",
    description: "Lose 5 matches by a narrow margin (3-4 or 4-5)",
    emoji: "😅",
    milestone: false,
  },
  // Milestone badges — never reset between seasons
  all_season_warrior: {
    name: "All-Season Warrior",
    description: "Play every single league day in a completed season",
    emoji: "⚔️",
    milestone: true,
  },
  division_champion: {
    name: "Division Champion",
    description: "Finish #1 in your division (manually awarded by admin)",
    emoji: "👑",
    milestone: true,
  },
  sp_team: {
    name: "SP Team",
    description: "Stuttgart Padel League family & friends (manually awarded)",
    emoji: "🏆",
    milestone: true,
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function BadgeSettings() {
  const { token } = useAuth();
  const [points, setPoints] = useState<Record<BadgeType, number>>(
    {} as Record<BadgeType, number>
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Sync badge points state
  const [syncPreviewOpen, setSyncPreviewOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncExecuting, setSyncExecuting] = useState(false);
  const [syncPreview, setSyncPreview] = useState<{
    totalPlayersAffected: number;
    totalPointsDelta: number;
    results: { playerId: string; playerName: string; changes: { type: string; oldPts: number; newPts: number; diff: number }[]; pointsDelta: number }[];
  } | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/badge-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load badge config");
      const data = await res.json();
      setPoints(data);
    } catch (e: any) {
      setError(e.message || "Failed to load badge config");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (type: BadgeType, value: string) => {
    const num = parseInt(value, 10);
    setPoints((prev) => ({ ...prev, [type]: isNaN(num) || num < 0 ? 0 : num }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess("");
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/badge-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(points),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to save");
      }
      setSuccess("Badge point values saved successfully.");
    } catch (e: any) {
      setError(e.message || "Failed to save badge config");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPreview = async () => {
    setSyncLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/badge-config/sync-points/preview`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch sync preview");
      const data = await res.json();
      setSyncPreview(data);
      setSyncPreviewOpen(true);
    } catch (e: any) {
      setError(e.message || "Failed to fetch sync preview");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncExecute = async () => {
    if (!syncPreview) return;
    setSyncExecuting(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/badge-config/sync-points/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ results: syncPreview.results }),
      });
      if (!res.ok) throw new Error("Failed to execute sync");
      const data = await res.json();
      setSuccess(`Synced badge points for ${data.playersUpdated} players. Total point adjustment: ${data.totalPointsAdjusted > 0 ? "+" : ""}${data.totalPointsAdjusted} pts.`);
      setSyncPreviewOpen(false);
      setSyncPreview(null);
    } catch (e: any) {
      setError(e.message || "Failed to execute sync");
    } finally {
      setSyncExecuting(false);
    }
  };

  const seasonalBadges = (Object.keys(BADGE_CATALOGUE) as BadgeType[]).filter(
    (k) => !BADGE_CATALOGUE[k].milestone
  );
  const milestoneBadges = (Object.keys(BADGE_CATALOGUE) as BadgeType[]).filter(
    (k) => BADGE_CATALOGUE[k].milestone
  );

  const totalSeasonalPoints = seasonalBadges.reduce(
    (sum, k) => sum + (points[k] ?? 0),
    0
  );
  const totalMilestonePoints = milestoneBadges.reduce(
    (sum, k) => sum + (points[k] ?? 0),
    0
  );

  const renderTable = (types: BadgeType[]) => (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell sx={{ fontWeight: "bold", width: 40 }}>
              Emoji
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Badge</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: "bold", width: 140 }}>
              Points
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {types.map((type) => {
            const info = BADGE_CATALOGUE[type];
            return (
              <TableRow key={type} hover>
                <TableCell sx={{ fontSize: 22, textAlign: "center" }}>
                  {info.emoji}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {info.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {info.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={points[type] ?? 0}
                    onChange={(e) => handleChange(type, e.target.value)}
                    inputProps={{ min: 0, max: 9999 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">pts</InputAdornment>
                      ),
                    }}
                    sx={{ width: 120 }}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <AppBar position="sticky" sx={{ bgcolor: "primary.main" }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Badge Settings
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
            sx={{ fontWeight: 700 }}
          >
            Save Changes
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {/* Info card */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                  <InfoOutlinedIcon color="info" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      How badge points work
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Points are added directly to a player's total points when a badge is earned.
                      The points value stored at the moment of earning is locked to that badge instance — changing
                      values here only affects <strong>future</strong> badges, not ones already awarded.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Seasonal badges</strong> reset each season, so players can earn (and receive points for)
                      them again every season. <strong>Milestone badges</strong> are permanent and only awarded once.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Seasonal badges */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Seasonal Badges
                </Typography>
                <Tooltip title="Max points a very active player could earn from seasonal badges in one season">
                  <Typography variant="body2" color="text.secondary">
                    Max {totalSeasonalPoints} pts possible
                  </Typography>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These badges reset each season. Players can earn them (and their points) anew every season.
              </Typography>
              {renderTable(seasonalBadges)}
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Milestone badges */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Milestone Badges
                </Typography>
                <Tooltip title="Lifetime maximum — each badge is awarded once ever">
                  <Typography variant="body2" color="text.secondary">
                    {totalMilestonePoints} pts once-ever
                  </Typography>
                </Tooltip>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These are permanent, career-level badges. They are never reset between seasons.
              </Typography>
              {renderTable(milestoneBadges)}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Sync Badge Points to Current Config
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updates <code>pointsAwarded</code> on all existing badges to match the values above, and adjusts player totals accordingly.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="warning"
                size="large"
                startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={handleSyncPreview}
                disabled={syncLoading || saving}
                sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Preview Sync
              </Button>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{ fontWeight: 700, px: 5 }}
              >
                Save Changes
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* Sync Preview Dialog */}
      <Dialog
        open={syncPreviewOpen}
        onClose={() => !syncExecuting && setSyncPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          🔄 Preview: Sync Badge Points to Current Config
        </DialogTitle>
        <DialogContent>
          {syncPreview && (
            <>
              <Alert severity={syncPreview.totalPlayersAffected === 0 ? "success" : "warning"} sx={{ mb: 2 }}>
                {syncPreview.totalPlayersAffected === 0 ? (
                  "All badge points are already up to date. No changes needed."
                ) : (
                  <>
                    <strong>{syncPreview.totalPlayersAffected} player(s)</strong> will have their badge points updated.&nbsp;
                    Net point adjustment across all players:&nbsp;
                    <strong style={{ color: syncPreview.totalPointsDelta >= 0 ? "green" : "red" }}>
                      {syncPreview.totalPointsDelta >= 0 ? "+" : ""}{syncPreview.totalPointsDelta} pts
                    </strong>
                  </>
                )}
              </Alert>

              {syncPreview.results.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>Player</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>Badge Changes</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }} align="right">Point Δ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {syncPreview.results.map((r) => (
                        <TableRow key={r.playerId}>
                          <TableCell>{r.playerName}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {r.changes.map((c, i) => (
                                <Chip
                                  key={i}
                                  label={`${c.type}: ${c.oldPts}→${c.newPts}`}
                                  size="small"
                                  color={c.diff > 0 ? "success" : "error"}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: "bold", color: r.pointsDelta >= 0 ? "success.main" : "error.main" }}
                            >
                              {r.pointsDelta >= 0 ? "+" : ""}{r.pointsDelta}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncPreviewOpen(false)} disabled={syncExecuting}>
            Cancel
          </Button>
          {syncPreview && syncPreview.totalPlayersAffected > 0 && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleSyncExecute}
              disabled={syncExecuting}
              startIcon={syncExecuting ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            >
              {syncExecuting ? "Syncing..." : `Confirm & Sync (${syncPreview.totalPlayersAffected} players)`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
